import React from 'react';

import { Text } from '@reckon-web/text';
import { TextLinkButton } from '@reckon-web/text-link';
import { makeId, plural } from '@reckon-web/utils';
import { Inline } from '@reckon-web/inline';
import { Flex } from '@reckon-web/flex';
import { EyeIcon, LockIcon, PauseIcon } from '@reckon-web/icon';

import { CountByTaskStatus } from '../../hooks/use-query-jobs-with-tasks';

import { TaskStatusEnum } from './types/task-status-enum';

export const WorkItemTitle: React.FC<{
  name?: string | null;
  locked?: boolean;
  onClick?: () => void;
  size?: 'small' | 'xsmall';
  weight?: 'regular' | 'semibold';
  testId?: string;
}> = ({
  name,
  locked,
  onClick,
  size = 'small',
  weight = 'semibold',
  testId = `work-item-title`,
}) => {
  return (
    <Inline alignY="center" gap={size}>
      {locked && (
        <LockIcon
          size="small"
          color="dim"
          data-test-id={makeId(testId, 'lock-icon')}
        />
      )}
      {name && onClick ? (
        <Text size={size} overflowStrategy="breakword" weight={weight}>
          <TextLinkButton
            color="base"
            onClick={onClick}
            data-testid={makeId(testId, 'text-link')}
          >
            {name}
          </TextLinkButton>
        </Text>
      ) : (
        <Text
          size={size}
          weight="medium"
          css={{
            // stop LONG text (usually links, but anything really) from busting out sideways
            overflowWrap: 'break-word',
            wordWrap: 'break-word',
            wordBreak: 'break-word',
          }}
          data={{ testid: makeId(testId, 'name') }}
        >
          {name}
        </Text>
      )}
    </Inline>
  );
};

export const CompletedTasks: React.FC<{
  counts: CountByTaskStatus;
  totalCount: number;
  size?: 'xsmall' | 'small';
  testId?: string;
}> = ({ counts, totalCount, size = 'small', testId = `completed-tasks` }) => {
  const countCompleted =
    (counts[TaskStatusEnum.valuesEnum.Done] || 0) +
    (counts[TaskStatusEnum.valuesEnum.Cancelled] || 0);
  const countCancelled = counts[TaskStatusEnum.valuesEnum.Cancelled] || 0;

  return (
    <Inline alignY="center" gap={size}>
      <Text
        size={size}
        weight="medium"
        data={{ testid: makeId(testId, 'completed') }}
      >
        {plural(countCompleted, 'completed task', 'completed tasks')}
      </Text>
      <Text
        size={size}
        weight="medium"
        color="dim"
        data={{ testid: makeId(testId, 'tasks') }}
      >
        (of {totalCount})
      </Text>
      {countCancelled > 0 && (
        <Text
          size={size}
          weight="medium"
          color="dim"
          data={{ testid: makeId(testId, 'cancelled') }}
        >
          ({countCancelled} cancelled)
        </Text>
      )}
    </Inline>
  );
};

export const IncompleteTasks: React.FC<{
  counts: CountByTaskStatus;
  totalCount: number;
  size?: 'xsmall' | 'small';
  testId?: string;
}> = ({ counts, totalCount, size = 'small', testId = `incomplete-tasks` }) => {
  const count =
    (counts[TaskStatusEnum.valuesEnum.NotStarted] || 0) +
    (counts[TaskStatusEnum.valuesEnum.InProgress] || 0) +
    (counts[TaskStatusEnum.valuesEnum.OnHold] || 0) +
    (counts[TaskStatusEnum.valuesEnum.ReadyForReview] || 0);

  return (
    <Inline alignY="center" gap={size}>
      <Text
        size={size}
        weight="medium"
        data={{ testid: makeId(testId, 'incomplete') }}
      >
        {plural(count, 'incomplete task', 'incomplete tasks')}
      </Text>
      <Text
        size={size}
        weight="medium"
        color="dim"
        data={{ testid: makeId(testId, 'tasks') }}
      >
        (of {totalCount})
      </Text>
    </Inline>
  );
};

export const OnHoldTasks: React.FC<{
  counts: CountByTaskStatus;
  size?: 'xsmall' | 'small';
  testId?: string;
}> = ({ counts, size = 'small', testId = `on-hold-tasks` }) => {
  const count = counts[TaskStatusEnum.valuesEnum.OnHold] || 0;
  if (count < 1) {
    return null;
  }

  return (
    <Flex alignItems="center" gap={size}>
      <PauseIcon size="small" color="cautious" />
      <Text
        size={size}
        weight="medium"
        data={{ testid: makeId(testId, 'text') }}
      >
        {count} on hold
      </Text>
    </Flex>
  );
};

export const ReadyForReviewTasks: React.FC<{
  counts: CountByTaskStatus;
  size?: 'xsmall' | 'small';
  testId?: string;
}> = ({ counts, size = 'small', testId = `review-ready-tasks` }) => {
  const count = counts[TaskStatusEnum.valuesEnum.ReadyForReview] || 0;

  return (
    <React.Fragment>
      {count > 0 && (
        <Flex alignItems="center" gap={size}>
          <EyeIcon size="small" color="positive" />
          <Text
            size={size}
            weight="medium"
            data={{ testid: makeId(testId, 'text') }}
          >
            {count} ready for review
          </Text>
        </Flex>
      )}
    </React.Fragment>
  );
};
